import { FormikErrors, useFormikContext } from 'formik';
import { useCallback, useEffect, useMemo } from 'react';
import { get } from 'lodash';

import { Badge } from '@@/Badge';

import { artifactConfigStepValidationSchema } from '../../validation';
import { WizardMode, WizardStep, useWizardContext } from '../../WizardContext';
import { StepTitle } from '../StepTitle';
import { ArtifactConfig, ArtifactFile, FormValues } from '../../types';
import {
  DeploymentType,
  DEPLOYMENT_TYPE_LABELS,
  UNKNOWN_DEPLOYMENT_TYPE,
} from '../../detectDeploymentType';
import { useDetectDeploymentType } from '../../useDetectDeploymentType';

import { AdvancedConfiguration } from './AdvancedConfiguration';
import { TargetsSelector } from './TargetsSelector';
import { UpdateConfiguration } from './UpdateConfiguration';
import { EnvironmentVariables } from './EnvironmentVariables';
import { RegistryConfiguration } from './RegistryConfiguration';
import { ComposeDeployOptions } from './ComposeDeployOptions';

export const ArtifactConfigStep: WizardStep = {
  id: 'config',
  label: 'Config',
  Component: ArtifactConfigStepView,
  validateStep,
};

export function ArtifactConfigStepView() {
  const { values } = useFormikContext<FormValues>();

  return (
    <div className="flex flex-col gap-8">
      {values.artifacts.map((_, index) => (
        <ArtifactConfigSection
          file={values.artifacts[index].files[0]}
          key={index}
          index={index}
        />
      ))}
    </div>
  );
}

function ArtifactConfigSection({
  index,
  file,
}: {
  index: number;
  file: ArtifactFile;
}) {
  const { setFieldValue } = useNamespacedFormikContext<
    FormValues,
    FormValues['artifacts'][number],
    Exclude<FormikErrors<FormValues>['artifacts'], string | undefined>[number]
  >(`artifacts[${index}]`);

  const { isLoading, data } = useDetectDeploymentType(file);
  useEffect(() => {
    if (!isLoading && !!data) {
      setFieldValue('deploymentType', data);
    }
  }, [data, index, isLoading, setFieldValue]);

  if (isLoading || !data) {
    return null;
  }

  if (data === UNKNOWN_DEPLOYMENT_TYPE) {
    return (
      <div className="flex flex-col">
        Unable to determine deployment type. The selected file does not look
        like a valid Docker Compose or Kubernetes manifest.
      </div>
    );
  }

  return <ArtifactConfigSectionNew index={index} />;
}

function ArtifactConfigSectionNew({ index }: { index: number }) {
  const isEditing = useWizardContext().mode === WizardMode.Edit;

  const {
    values,
    errors: artifactErrors,
    setFieldValue,
    setFieldError,
  } = useNamespacedFormikContext<
    FormValues,
    FormValues['artifacts'][number],
    Exclude<FormikErrors<FormValues>['artifacts'], string | undefined>[number]
  >(`artifacts[${index}]`);

  const errors =
    typeof artifactErrors === 'string' ? undefined : artifactErrors;

  const setConfigValue = useCallback(
    <K extends keyof ArtifactConfig>(f: K, v: ArtifactConfig[K]) =>
      setFieldValue('config', { ...values.config, [f]: v }),
    [values.config, setFieldValue]
  );

  return (
    <section aria-label={`Configure ${values.name}`}>
      <StepTitle
        title={
          <>
            Configure {values.name}{' '}
            <Badge>{DEPLOYMENT_TYPE_LABELS[values.deploymentType]}</Badge>
          </>
        }
        subtitle="Choose the deployment targets and settings for this stack."
      />

      <div className="flex flex-col gap-4">
        <TargetsSelector
          values={values.targets}
          onChange={(targets) => setFieldValue('targets', targets)}
          deploymentType={values.deploymentType}
        />

        <AdvancedConfiguration
          values={values.config}
          setFieldValue={setConfigValue}
          // TODO here we support only one file, we should probably show this only for values.files of type edge config or something like that?
          refName={values.files[0]?.ref}
          sourceId={values.files[0]?.sourceId}
          errors={errors?.config}
          deploymentType={values.deploymentType}
          isEditing={isEditing}
        />

        {values.deploymentType === DeploymentType.Compose && (
          <EnvironmentVariables
            values={values.config.envVars}
            onChange={(value) => setConfigValue('envVars', value)}
            errors={errors?.config?.envVars}
          />
        )}

        <RegistryConfiguration
          values={values.config}
          setFieldValue={(f, v) => setConfigValue(f, v)}
          onRegistryError={(message) =>
            setFieldError(`config.registryId`, message)
          }
          errors={errors?.config}
        />

        {values.deploymentType === DeploymentType.Compose && (
          <ComposeDeployOptions
            values={values.config}
            setFieldValue={setConfigValue}
          />
        )}

        <UpdateConfiguration
          values={values.config.parallelConfig}
          onChange={(config) =>
            setConfigValue('parallelConfig', {
              ...values.config.parallelConfig,
              ...config,
            })
          }
          errors={errors?.config?.parallelConfig}
        />
      </div>
    </section>
  );
}

function validateStep() {
  return artifactConfigStepValidationSchema();
}

function useNamespacedFormikContext<T, K, E>(namespace: string) {
  const {
    values: rootValues,
    errors: rootErrors,
    setFieldValue: rootSetFieldValue,
    setFieldError: rootSetFieldError,
    ...context
  } = useFormikContext<T>();

  const setFieldValue: typeof rootSetFieldValue = useCallback(
    (field, ...params) => rootSetFieldValue(`${namespace}.${field}`, ...params),
    [namespace, rootSetFieldValue]
  );

  const setFieldError: typeof rootSetFieldError = useCallback(
    (field, ...params) => rootSetFieldError(`${namespace}.${field}`, ...params),
    [namespace, rootSetFieldError]
  );

  const values: K = useMemo(
    () => get(rootValues, namespace),
    [namespace, rootValues]
  );
  const errors: E = useMemo(
    () => get(rootErrors, namespace),
    [namespace, rootErrors]
  );

  return useMemo(
    () => ({
      ...context,
      values,
      errors,
      setFieldValue,
      setFieldError,
      root: {
        values: rootValues,
        errors: rootErrors,
        setFieldValue: rootSetFieldValue,
        setFieldError: rootSetFieldError,
      },
    }),
    [
      context,
      errors,
      rootErrors,
      rootSetFieldError,
      rootSetFieldValue,
      rootValues,
      setFieldError,
      setFieldValue,
      values,
    ]
  );
}
